<template>
  <div class="variable-mention-editor">
    <div
      class="editor-shell"
      :class="{
        'editor-shell--show-limit': showLimit,
        'editor-shell--fixed-height': hasFixedHeight
      }"
      :style="editorShellStyle"
    >
      <div
        ref="editorContainerRef"
        class="editor-container"
        data-testid="variable-mention-editor-container"
        @paste.capture="handlePaste"
        @click="handleEditorContainerClick"
      >
        <slot v-if="isInEditorContainer" name="cardbar" :editor="editorRef" />
        <EchoEditor
          ref="editorRef"
          v-bind="filteredProps"
          :model-value="editorHtml"
          class="form-item-editor"
          output="html"
          :extensions="extensions"
          :hide-menubar="true"
          :hide-toolbar="true"
          :hide-bubble="true"
          :show-limit="showLimit"
          @update:modelValue="handleEditorHtmlUpdate"
          @change="handleChange"
        />
      </div>
      <div v-if="!disabled" class="resize-handle" @mousedown="startResize">
        <BotIcon name="config-size" width="8" height="8" padding="0" />
      </div>
    </div>
    <div
      v-if="showInsertAction && insertActionOptions.length"
      class="editor-action"
    >
      <u-dropdown v-if="!disabled" trigger="click" placement="bottom-end">
        <div class="insert-variable-action">
          <BotIcon
            name="add-fill"
            :padding="0"
            size="16"
            class="insert-variable-icon"
          />
          <span class="insert-variable-text">{{
            $t(
              'views.voiceAgent.setting.components.VoiceAgentBaseForm.2e6d0f2f9da53ee037f55c1fcec43545'
            )
          }}</span>
        </div>
        <template #dropdown>
          <u-dropdown-menu
            :options="insertActionOptions"
            data-testid="insert-variable-menu"
            @click="handleInsertVariable"
          />
        </template>
      </u-dropdown>
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  watch,
  onMounted,
  onUnmounted,
  onBeforeUnmount
} from 'vue';
import { EchoEditor, BaseKit, Fullscreen, MentionCommand } from 'vix-editor';
import BotIcon from '@/components/BotIcon.vue';
import { createVariableSuggestions } from './suggestion';

const escapeHtml = text =>
  String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const legacyBlockTags = new Set(['DIV', 'P', 'LI']);

const normalizeLegacyMentionHtml = value => {
  const source = String(value || '');
  const hasLegacyMarkup = /<\/?(?:div|br)\b|rich-mention__span/i.test(source);
  if (!hasLegacyMarkup || typeof DOMParser === 'undefined') {
    return source;
  }

  const normalizedSource = source.replace(
    /<div>\s*<br\s*\/?>\s*<\/div>/gi,
    '\n'
  );
  const doc = new DOMParser().parseFromString(normalizedSource, 'text/html');

  const readNodeText = node => {
    if (node.nodeType === 3) {
      return node.textContent || '';
    }
    if (node.nodeType !== 1) {
      return '';
    }

    if (node.classList.contains('rich-mention__span')) {
      return (
        node.getAttribute('data-name') ||
        node.textContent ||
        node.getAttribute('data-id') ||
        ''
      );
    }
    if (node.tagName === 'BR') {
      return '\n';
    }

    const text = Array.from(node.childNodes).map(readNodeText).join('');
    return legacyBlockTags.has(node.tagName) ? `${text}\n` : text;
  };

  return Array.from(doc.body.childNodes)
    .map(readNodeText)
    .join('')
    .replace(/\u200B/g, '')
    .replace(/\u00A0/g, ' ')
    .replace(/\n+$/, '');
};

const convertLineToHtml = line => {
  const mentionPattern = /\{\{([^{}]+)\}\}/g;
  let lastIndex = 0;
  let html = '';
  let match = mentionPattern.exec(line);

  while (match) {
    const [raw, variableKey] = match;
    html += escapeHtml(line.slice(lastIndex, match.index));
    html += `<span class="mention rich-mention__span" data-type="mention" data-id="${escapeHtml(
      variableKey
    )}" data-name="${escapeHtml(variableKey)}" data-label="${escapeHtml(
      variableKey
    )}" data-mention-suggestion-char="{">${escapeHtml(raw)}</span>`;
    lastIndex = match.index + raw.length;
    match = mentionPattern.exec(line);
  }

  html += escapeHtml(line.slice(lastIndex));
  return html;
};

const convertTextToEditorHtml = text => {
  if (!text) {
    return '';
  }

  return normalizeLegacyMentionHtml(text)
    .split('\n')
    .map(line => `<p>${convertLineToHtml(line)}</p>`)
    .join('');
};

const toCssUnit = value => {
  if (value == null || value === '') {
    return undefined;
  }
  return typeof value === 'number' ? `${value}px` : value;
};

const toPixelValue = value => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const matchedValue = Number.parseFloat(value);
  return Number.isFinite(matchedValue) ? matchedValue : null;
};

const normalizeOption = item => {
  if (typeof item === 'string') {
    return {
      label: item,
      value: item
    };
  }
  if (!item) {
    return null;
  }

  const value = item.value ?? item.label;
  if (!value) {
    return null;
  }

  return {
    ...item,
    label: item.label ?? value,
    value
  };
};

const getMentionText = attrs => {
  const textValue = attrs?.textValue;
  if (typeof textValue === 'string' && textValue && textValue !== 'null') {
    return textValue;
  }

  const variableKey = [attrs?.id, attrs?.name, attrs?.label].find(
    value => value != null && value !== '' && value !== 'null'
  );
  if (!variableKey) {
    return '';
  }

  const normalizedKey = String(variableKey)
    .replace(/^\{\{/, '')
    .replace(/\}\}$/, '');
  return `{{${normalizedKey}}}`;
};

export default defineComponent({
  name: 'VariableMentionEditor',
  components: {
    EchoEditor,
    BotIcon
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    variables: {
      type: Array,
      default: () => []
    },
    config: {
      type: Object,
      default: null
    },
    popperOptions: {
      type: Object,
      default: null
    },
    transformFn: {
      type: Function,
      default: null
    },
    height: {
      type: [Number, String],
      default: 80
    },
    minHeight: {
      type: [Number, String],
      default: 40
    },
    maxHeight: {
      type: [Number, String],
      default: undefined
    },
    maxLength: {
      type: Number,
      default: 500000
    },
    showLimit: {
      type: Boolean,
      default: false
    },
    showInsertAction: {
      type: Boolean,
      default: false
    },
    insertTrigger: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'blur', 'focus', 'change'],
  setup(props, { emit, expose }) {
    const editorRef = ref(null);
    const editorContainerRef = ref(null);

    const isInEditorContainer = ref(false);
    const resizedHeight = ref(null);

    let removeResizeListeners = null;
    let lastInternalValue;

    const normalizedVariables = computed(() =>
      (props.variables || []).map(normalizeOption).filter(Boolean)
    );

    const normalizedMentionOptions = computed(() => {
      if (props.config && typeof props.config === 'object') {
        const entries = Object.entries(props.config)
          .filter(([, items]) => Array.isArray(items))
          .map(([char, items]) => [
            char,
            items.map(normalizeOption).filter(Boolean)
          ]);

        if (entries.length) {
          return Object.fromEntries(entries);
        }
      }

      return {
        '{': normalizedVariables.value
      };
    });

    const defaultMentionChar = computed(
      () =>
        props.insertTrigger ||
        Object.keys(normalizedMentionOptions.value)[0] ||
        '{'
    );

    const suggestionConfigs = computed(() => {
      const suggestions = createVariableSuggestions(
        () => normalizedMentionOptions.value
      );

      if (suggestions.length) {
        return suggestions;
      }

      return [
        {
          char: defaultMentionChar.value,
          allowedPrefixes: null,
          items: () => [],
          render: () => ({})
        }
      ];
    });

    const insertActionOptions = computed(() => {
      return normalizedVariables.value;
    });

    const editorHtml = ref(convertTextToEditorHtml(props.modelValue));

    const syncEditorValue = value => {
      editorHtml.value = convertTextToEditorHtml(value);
    };

    const handleEditorHtmlUpdate = value => {
      if (typeof value === 'string') {
        editorHtml.value = value;
      }
    };

    watch(
      () => props.modelValue,
      value => {
        if (value === lastInternalValue) {
          lastInternalValue = undefined;
          return;
        }

        lastInternalValue = undefined;
        syncEditorValue(value);
      }
    );

    const currentHeight = computed(() =>
      toCssUnit(resizedHeight.value ?? props.height)
    );
    const effectiveMinHeight = computed(() => {
      const heightValue = toPixelValue(props.height);
      const minHeightValue = toPixelValue(props.minHeight);

      if (
        heightValue != null &&
        minHeightValue != null &&
        heightValue < minHeightValue
      ) {
        return heightValue;
      }

      return props.minHeight;
    });
    const minHeightStyle = computed(() => toCssUnit(effectiveMinHeight.value));
    const maxHeightStyle = computed(() => toCssUnit(props.maxHeight));

    const editorShellStyle = computed(() => {
      if (props.showLimit) {
        return {
          height: 'auto'
        };
      }

      return {
        height: currentHeight.value,
        minHeight: minHeightStyle.value,
        maxHeight: maxHeightStyle.value
      };
    });

    // Only a height-limited editor can scroll; auto-height ones keep growing.
    const hasFixedHeight = computed(() => {
      const height = currentHeight.value;
      return !props.showLimit && height != null && height !== 'auto';
    });

    const buildMentionText = option => {
      const normalizedOption = normalizeOption(option);
      if (!normalizedOption) {
        return '';
      }

      const transformedText = props.transformFn?.(normalizedOption);
      if (typeof transformedText === 'string' && transformedText) {
        return transformedText;
      }

      return `{{${normalizedOption.value}}}`;
    };

    const buildMentionAttrs = (option, trigger = defaultMentionChar.value) => {
      const normalizedOption = normalizeOption(option);
      if (!normalizedOption) {
        return null;
      }

      return {
        id: normalizedOption.value,
        name: normalizedOption.label || normalizedOption.value,
        label: normalizedOption.value,
        textValue: buildMentionText(normalizedOption),
        mentionSuggestionChar: trigger
      };
    };

    const insertMentionContent = ({
      editor,
      option,
      trigger = defaultMentionChar.value,
      range
    }) => {
      const attrs = buildMentionAttrs(option, trigger);
      if (!editor || !attrs) {
        return false;
      }

      const chain = editor.chain().focus();
      if (range) {
        chain.insertContentAt(range, [
          {
            type: 'mention',
            attrs
          }
        ]);
      } else {
        chain.insertContent([
          {
            type: 'mention',
            attrs
          }
        ]);
      }
      chain.run();
      return true;
    };

    const extensions = computed(() => [
      BaseKit.configure({
        placeholder: {
          placeholder: ({ editor }) =>
            editor.isEmpty ? props.placeholder : '',
          showOnlyCurrent: true
        },
        characterCount: {
          limit: props.maxLength
        }
      }),
      Fullscreen.configure({
        spacer: true
      }),
      MentionCommand.configure({
        HTMLAttributes: {
          class: 'mention'
        },
        renderText({ node }) {
          return getMentionText(node.attrs);
        },
        renderHTML({ node, options }) {
          // vix-editor calls the option-level renderHTML with
          // { options, node, suggestion }; the merged data-* attributes live on
          // options.HTMLAttributes. Destructuring HTMLAttributes here yields
          // undefined and strips data-id/data-name/data-label from the
          // serialized HTML, breaking mention round-trips.
          const attrs = options?.HTMLAttributes || {};
          return [
            'span',
            {
              ...attrs,
              class: `${attrs.class || ''} rich-mention__span`.trim(),
              'data-type': 'mention'
            },
            getMentionText(node.attrs)
          ];
        },
        suggestions: suggestionConfigs.value.map(suggestion => ({
          ...suggestion,
          command: ({ editor, range, props: suggestionProps }) => {
            insertMentionContent({
              editor,
              range,
              option: suggestionProps,
              trigger: suggestion.char
            });
          }
        }))
      })
    ]);

    const filteredProps = computed(() => {
      const {
        modelValue,
        variables,
        config,
        popperOptions,
        transformFn,
        maxLength,
        height,
        minHeight,
        maxHeight,
        showInsertAction,
        insertTrigger,
        ...rest
      } = props;

      return {
        ...rest,
        limit: maxLength,
        minHeight: currentHeight.value,
        maxHeight: currentHeight.value
      };
    });

    const isFocused = computed(() => editorRef.value?.editor?.isFocused);

    watch(isFocused, (newValue, oldValue) => {
      if (oldValue === true && newValue === false) {
        emit('blur');
      }
      if (oldValue === false && newValue === true) {
        emit('focus');
        isInEditorContainer.value = true;
      }
    });

    const handleEditorContainerClick = () => {
      isInEditorContainer.value = true;
    };

    const handlePaste = event => {
      const pastedText = event.clipboardData?.getData('text/plain') || '';
      if (!/\{\{[^{}]+\}\}/.test(pastedText)) {
        return;
      }

      const editor = editorRef.value?.editor;
      if (!editor) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      const content = [];
      const mentionPattern = /\{\{([^{}]+)\}\}/g;
      let lastIndex = 0;
      let match;
      while ((match = mentionPattern.exec(pastedText))) {
        if (match.index > lastIndex) {
          content.push({
            type: 'text',
            text: pastedText.slice(lastIndex, match.index)
          });
        }
        const variableKey = match[1];
        content.push({
          type: 'mention',
          attrs: buildMentionAttrs({
            label: variableKey,
            value: variableKey
          })
        });
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < pastedText.length) {
        content.push({
          type: 'text',
          text: pastedText.slice(lastIndex)
        });
      }
      editor.chain().focus().insertContent(content).run();
    };

    const handleGlobalClick = event => {
      if (
        editorContainerRef.value &&
        !editorContainerRef.value.contains(event.target)
      ) {
        isInEditorContainer.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleGlobalClick);
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleGlobalClick);
    });

    onBeforeUnmount(() => {
      removeResizeListeners?.();
      removeResizeListeners = null;
    });

    const appendVariable = option => {
      const nextText = buildMentionText(option);
      emit(
        'update:modelValue',
        `${normalizeLegacyMentionHtml(props.modelValue)}${nextText}`
      );
    };

    const insertVariable = (option, trigger = defaultMentionChar.value) => {
      const normalizedOption = normalizeOption(option);
      if (!normalizedOption) {
        return false;
      }

      const editor = editorRef.value?.editor;
      if (!editor) {
        appendVariable(normalizedOption);
        return true;
      }

      return insertMentionContent({
        editor,
        option: normalizedOption,
        trigger
      });
    };

    const handleInsertVariable = option => {
      insertVariable(option, defaultMentionChar.value);
    };

    const handleChange = payload => {
      const textValue =
        payload?.editor?.getText?.({ blockSeparator: '\n' }) ??
        normalizeLegacyMentionHtml(props.modelValue);
      lastInternalValue = textValue;
      emit('update:modelValue', textValue);
      emit('change', textValue);
    };

    const startResize = event => {
      if (props.disabled) {
        return;
      }

      event.preventDefault();

      const startY = event.clientY;
      const startHeight =
        resizedHeight.value ??
        toPixelValue(props.height) ??
        editorContainerRef.value?.getBoundingClientRect?.().height ??
        0;
      const minHeight = toPixelValue(effectiveMinHeight.value) ?? 40;
      const maxHeight = toPixelValue(props.maxHeight);

      const handleMouseMove = moveEvent => {
        const deltaY = moveEvent.clientY - startY;
        let nextHeight = startHeight + deltaY;
        nextHeight = Math.max(minHeight, nextHeight);
        if (maxHeight != null) {
          nextHeight = Math.min(maxHeight, nextHeight);
        }
        resizedHeight.value = nextHeight;
      };

      const handleMouseUp = () => {
        removeResizeListeners?.();
        removeResizeListeners = null;
      };

      removeResizeListeners?.();
      removeResizeListeners = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    const insertMentionByOption = (option, trigger) =>
      insertVariable(option, trigger);

    expose({
      insertMentionByOption
    });

    return {
      editorHtml,
      editorRef,
      editorContainerRef,
      isInEditorContainer,
      extensions,
      filteredProps,
      editorShellStyle,
      hasFixedHeight,
      insertActionOptions,
      showInsertAction: props.showInsertAction,
      handleEditorContainerClick,
      handlePaste,
      handleInsertVariable,
      handleEditorHtmlUpdate,
      handleChange,
      startResize
    };
  }
});
</script>

<style lang="less" scoped>
.variable-mention-editor {
  width: 100%;
}

.editor-shell {
  position: relative;
}

.editor-shell--show-limit {
  .editor-container {
    height: auto;

    :deep(.echo-editor),
    :deep(.form-item-editor) {
      height: auto;
    }

    :deep(
        .form-item-editor
          > .relative.flex.flex-col.overflow-hidden
          > .flex.justify-between.p-3.items-center
      ) {
      height: 32px;
      flex-shrink: 0;
      padding: 0 12px;
      color: var(--color-text-icon-body-prompt);
      font-size: 12px;
      line-height: 32px;
    }
  }
}

// EchoEditor nests the editable area in an auto-height, overflow-hidden
// wrapper; give the chain a definite height so .tiptap can scroll with the
// wheel instead of being clipped by an ancestor.
.editor-shell--fixed-height {
  :deep(.echo-editor > .relative.flex.flex-col.overflow-hidden) {
    height: 100%;
    min-height: 0;
  }

  :deep(.echo-editor > .relative.flex.flex-col.overflow-hidden > .relative.flex-1) {
    flex: 1;
    min-height: 0;
  }

  :deep(
      .echo-editor
        > .relative.flex.flex-col.overflow-hidden
        > .relative.flex-1
        > div:first-child
    ) {
    height: 100%;
    min-height: 0;
  }
}

.editor-container {
  height: 100%;

  :deep(.echo-editor) {
    background: transparent;
    height: 100%;
  }

  :deep(.form-item-editor) {
    height: 100%;
    border-radius: 8px;
    border: 1px solid var(--border-neutral-color-secondary);
    background: var(--form-background-color, transparent);

    &:hover,
    &:focus-within {
      border-color: var(--color-border-input-border-hover);
    }
  }

  :deep(.tiptap) {
    padding: 8px;
    min-height: 100%;
    height: 100%;
    color: var(--active-font-color);
    caret-color: var(--active-font-color);
    outline: none;
    overflow-y: auto;

    p {
      line-height: 22px;

      &.is-editor-empty:first-child:before,
      &.is-empty:first-child:before {
        color: var(--placeholder-color);
      }
    }
  }

  :deep(.mention),
  :deep(.rich-mention__span) {
    display: inline-flex;
    align-items: center;
    min-height: 20px;
    padding: 0 1px;
    color: var(--mention-theme-color, var(--theme-color));
    line-height: 20px;
    white-space: nowrap;
    user-select: all;
    vertical-align: baseline;
  }

  :deep(.mention.ProseMirror-selectednode),
  :deep(.rich-mention__span.ProseMirror-selectednode) {
    background: transparent;
  }
}

.resize-handle {
  position: absolute;
  right: 2px;
  bottom: 2px;
  z-index: 2;
  cursor: se-resize;
  color: var(--placeholder-color);

  &:hover {
    color: var(--theme-color);
  }
}

.editor-action {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.insert-variable-action {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
}

.insert-variable-icon {
  color: var(--theme-color);
}

.insert-variable-text {
  font-size: 12px;
  color: var(--theme-color);
}
</style>
